import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

class LeagueService {
  getAuthHeaders() {
    const token = localStorage.getItem('moneysmart-token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  // Get all leagues
  async getLeagues() {
    try {
      const response = await axios.get(`${API_BASE_URL}/leagues`, this.getAuthHeaders());
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get single league
  async getLeague(leagueId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/leagues/${leagueId}`, this.getAuthHeaders());
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Create league (Admin only)
  async createLeague(leagueData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/leagues`, leagueData, this.getAuthHeaders());
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Delete league / classroom (admin level 3+ only)
  async deleteLeague(leagueId) {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/leagues/${leagueId}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Assign teacher to league (Admin only)
  async assignTeacher(leagueId, teacherId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/teachers`,
        { teacherId },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Join league using league code (Teacher only)
  async joinLeague(code) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/join`,
        { code },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Remove teacher from league (Admin only)
  async removeTeacher(leagueId, teacherId) {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/leagues/${leagueId}/teachers/${teacherId}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Create student account (Teacher or Admin)
  async createStudent(leagueId, studentData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/students`,
        studentData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get league analytics (ED2D compliant)
  async getLeagueAnalytics(leagueId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/${leagueId}/analytics`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Make user a teacher (Admin only)
  async makeTeacher(userId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/make-teacher/${userId}`,
        {},
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Remove teacher status (Admin only)
  async removeTeacherStatus(userId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/remove-teacher/${userId}`,
        {},
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get all teachers (Admin only)
  async getTeachers() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/teachers/list`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get teacher KPIs / advanced analytics (Admin: any teacher; Teacher: self via "me")
  async getTeacherKPIs(teacherId = 'me') {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/teachers/${teacherId}/kpis`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get individual student analytics (Teacher or Admin)
  async getStudentAnalytics(studentId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/students/${studentId}/analytics`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get student's own class (for students to view their class)
  async getMyClass() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/my-class`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      // Re-throw with the full error object including status
      const errorObj = error.response?.data || { error: error.message };
      errorObj.status = error.response?.status;
      throw errorObj;
    }
  }

  // Get student's own assignments (for students to view their assignments)
  async getMyAssignments() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/my-assignments`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get available assignments for teachers to assign (Teacher or Admin)
  async getAvailableAssignments(leagueId = null) {
    try {
      const url = leagueId 
        ? `${API_BASE_URL}/leagues/assignments/available?leagueId=${leagueId}`
        : `${API_BASE_URL}/leagues/assignments/available`;
      const response = await axios.get(url, this.getAuthHeaders());
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Get assignments for a league (Teacher or Admin)
  async getLeagueAssignments(leagueId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/leagues/${leagueId}/assignments`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Assign lesson or quiz to league (Teacher or Admin)
  async assignToLeague(leagueId, assignmentData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/assignments`,
        assignmentData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Remove assignment from league (Teacher or Admin)
  async removeAssignment(leagueId, assignmentData) {
    try {
      const config = {
        ...this.getAuthHeaders(),
        data: assignmentData
      };
      const response = await axios.delete(
        `${API_BASE_URL}/leagues/${leagueId}/assignments`,
        config
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Bulk assign course or unit (Teacher or Admin)
  async bulkAssignToLeague(leagueId, bulkAssignmentData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/leagues/${leagueId}/assignments/bulk`,
        bulkAssignmentData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new LeagueService();
